<template>
  <div class="qr-scanner-page">
    <div class="scanner-header">
      <q-btn flat round icon="close" @click="handleClose" />
      <div class="text-h6">Сканирование QR кода</div>
    </div>

    <div class="scanner-container">
      <select
        v-if="showCameraSelector"
        v-model="selectedCamera"
        @change="switchCamera"
        class="camera-selector"
      >
        <option v-for="camera in cameras" :key="camera.id" :value="camera.id">
          {{ camera.label || `Camera ${camera.index + 1}` }}
        </option>
      </select>

      <div v-if="isInitializing" class="loading-message">
        Инициализация камеры...
      </div>

      <div v-else-if="error" class="error-message">
        {{ error }}
        <q-btn
          flat
          label="Повторить"
          color="white"
          @click="initializeScanner"
        />
      </div>

      <div id="reader"></div>
    </div>

    <div class="scanner-footer">
      <q-btn flat label="Отмена" color="primary" @click="handleClose" />
    </div>
  </div>
</template>

<script>
import {
  Html5QrcodeScanner,
  Html5QrcodeScanType,
  Html5Qrcode,
} from "html5-qrcode";
import { scannerTranslator } from "@/utils/qrScannerTranslator";

export default {
  data() {
    return {
      scanner: null,
      error: null,
      isInitializing: false,
      cameras: [],
      selectedCamera: null,
      showCameraSelector: false,
    };
  },
  mounted() {
    this.initializeScanner();
    scannerTranslator();
  },
  methods: {
    async initializeScanner() {
      try {
        this.isInitializing = true;
        this.error = null;

        this.cameras = await Html5Qrcode.getCameras();
        if (this.cameras.length === 0) {
          this.handleCameraError(
            new Error("Камера не найдена. Пожалуйста, проверьте подключение.")
          );
          throw new Error("No cameras found");
        }

        const html5QrCode = new Html5Qrcode("reader");
        this.scanner = html5QrCode;

        // Start with back camera or first available
        const backCamera = this.cameras.find(
          (cam) =>
            cam.label.toLowerCase().includes("back") ||
            cam.label.toLowerCase().includes("задняя") ||
            cam.label.toLowerCase().includes("rear")
        );
        const cameraId = backCamera?.id || this.cameras[0].id;
        this.selectedCamera = cameraId;

        await html5QrCode.start(
          cameraId,
          {
            fps: 10,
            qrbox: {
              width: window.innerWidth * 0.6,
              height: window.innerWidth * 0.6,
            },
            aspectRatio: 1.0,
            showTorchButtonIfSupported: true,
            rememberLastUsedCamera: true,
            supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          },
          (decodedText) => this.handleScanSuccess(decodedText)
        );

        if (this.cameras.length > 1) {
          this.showCameraSelector = true;
        }
      } catch (error) {
        this.handleCameraError(error);
      } finally {
        this.isInitializing = false;
      }
    },

    async switchCamera() {
      if (!this.selectedCamera || !this.scanner) return;

      try {
        await this.scanner.stop();
        await this.scanner.start(
          this.selectedCamera,
          {
            fps: 10,
            qrbox: {
              width: window.innerWidth * 0.6,
              height: window.innerWidth * 0.6,
            },
            aspectRatio: 1.0,
            showTorchButtonIfSupported: true,
            rememberLastUsedCamera: true,
            supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          },
          (decodedText) => this.handleScanSuccess(decodedText)
        );
      } catch (error) {
        this.handleCameraError(error);
      }
    },

    handleScanSuccess(decodedText) {
      this.$emit("qr-scanned", decodedText);
      this.handleClose();
    },
    handleCameraError(error) {
      this.error = error.message || "Ошибка инициализации камеры";
      this.$q.notify({
        color: "warning",
        message: error.message || "Ошибка камеры",
      });
    },
    stopScanner() {
      return new Promise((resolve) => {
        if (!this.scanner) {
          resolve();
          return;
        }

        // Handle both Html5Qrcode and Html5QrcodeScanner
        const stopOperation = this.scanner.stop
          ? this.scanner.stop()
          : this.scanner.clear();

        stopOperation
          .then(() => {
            console.log("Scanner stopped successfully");
            this.scanner = null;
            resolve();
          })
          .catch((error) => {
            console.error("Error stopping scanner:", error);
            this.scanner = null;
            resolve(); // Still resolve to allow cleanup to continue
          });
      });
    },
    async handleClose() {
      await this.stopScanner();
      this.$emit("close");
      this.$emit("cancel");
    },
  },
  async beforeUnmount() {
    await this.stopScanner();
  },
};
</script>

<style scoped>
.qr-scanner-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-width: 1200px;
  margin: 0 auto;
}

.scanner-header {
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.back-button {
  margin-right: 1rem;
}

.scanner-container {
  position: relative;
  align-items: center;
  justify-content: center;
  background-color: #000;
}

#reader {
  width: 100%;
  height: 100%;
}

.scanner-footer {
  padding: 1rem;
  background-color: white;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.error-message,
.loading-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  text-align: center;
  z-index: 1000;
}

@media (min-width: 768px) {
  .scanner-container {
    max-width: 1200px;
  }
}
#reader__scan_region {
  border: 2px solid white !important;
}
</style>
